import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { v4 as uuid4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, DescriptionTable, Notification } from 'shared/components';
import { getSectionListData, hasPermission } from 'shared/helpers';
import { authMethods } from 'shared/constants';
import { getUserProducts } from 'redux/user/actions';
import { generateLicense } from '../../actions';
import { getCustomerData, getResolvedOrderId } from './helpers';
import SelectedProductsTable from '../SelectedProductsTable';
import SelectedBundlesTable from '../SelectedBundlesTable';
import './styles.scss';

const VerifyOrderStep = ({
  companyID,
  formState,
  handleBack,
  handleOrderCreate,
  loading,
  orderID,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const canManageCustomerAccounts = hasPermission('manage_customer_accounts');
  const userID = useSelector((state) => get(state, 'user.details.id', ''));
  const productsSectionState = useSelector((state) =>
    userID ? get(state, `user.userSections.${userID}.products`, {}) : {},
  );
  const legacyProducts = useSelector((state) =>
    get(state, 'user.details.products', []),
  );

  useEffect(() => {
    if (!userID) {
      return;
    }

    dispatch(getUserProducts(userID));
  }, [dispatch, userID]);

  const productsSectionData = getSectionListData(
    get(productsSectionState, 'data', []),
  );
  const hasProductsSectionData =
    Array.isArray(get(productsSectionState, 'data')) ||
    Array.isArray(get(get(productsSectionState, 'data'), 'results'));

  const userProducts = get(productsSectionState, 'forbidden')
    ? []
    : hasProductsSectionData
      ? productsSectionData
      : legacyProducts;

  // eslint-disable-next-line no-unused-vars
  const counter = useSelector((state) => get(state, 'user.counter', []));
  const [generatingLicenses, setGeneratingLicenses] = useState(false);

  const isBundle = get(formState, 'is_bundle');
  const bundle = get(formState, 'bundle');

  const products = get(formState, 'products', []);
  const existingCustomer = get(formState, 'existingCustomer');
  const customerData = {
    email: getCustomerData(
      formState,
      existingCustomer,
      'customerEmail',
      'email',
    ),
    first_name: getCustomerData(
      formState,
      existingCustomer,
      'customerFirstName',
      'first_name',
    ),
    last_name: getCustomerData(
      formState,
      existingCustomer,
      'customerLastName',
      'last_name',
    ),
    company_name: getCustomerData(
      formState,
      existingCustomer,
      'customerOrganization',
      'company_name',
    ),
    address: getCustomerData(
      formState,
      existingCustomer,
      'customerAddress',
      'address',
    ),
    city: getCustomerData(formState, existingCustomer, 'customerCity', 'city'),
    state: getCustomerData(
      formState,
      existingCustomer,
      'customerState',
      'state',
    ),
    country: getCustomerData(
      formState,
      existingCustomer,
      'customerCountry',
      'country',
    ),
    postcode: getCustomerData(
      formState,
      existingCustomer,
      'customerZipcode',
      'postcode',
    ),
    phone: getCustomerData(
      formState,
      existingCustomer,
      'customerPhoneNumber',
      'phone',
    ),
    reference: getCustomerData(
      formState,
      existingCustomer,
      'customerReference',
      'reference',
    ),
    is_manager: get(formState, 'is_manager'),
  };

  const handleBackClick = () => handleBack();

  const generateLicenseKeys = async (productCode, licenseNum) => {
    let licenses = null;

    try {
      licenses = await generateLicense(companyID, productCode, licenseNum);
    } catch (error) {
      Notification('error', t('Error occured'));
    }

    return get(licenses, 'data');
  };

  const findTotalNumberOfLicenses = (orderItems, productID) => {
    // Filter the products array to find the ones with the given product code
    const orderLicenses = orderItems.filter(
      (oi) => get(oi, 'product_id') === productID,
    );

    // Initialize the count of licenses
    let totalLicenses = 0;

    // Loop through the matching products and add the number of licenses for each product
    orderLicenses.forEach((ol) => {
      totalLicenses += ol.licenses.length;
    });

    return totalLicenses;
  };

  const validateLicenseNum = (orderItems) => {
    counter.forEach((c) => {
      const productID = get(c, 'product.id');
      const maxLicenses = get(c, 'num_of_licenses');

      if (maxLicenses === null) {
        return true;
      }
      const createdLicenses = get(c, 'created_licenses_count');
      const remainingLicenses = maxLicenses - createdLicenses;
      const productLicensesCount = findTotalNumberOfLicenses(
        orderItems,
        productID,
      );
      const newCount = createdLicenses + productLicensesCount;
      if (newCount > maxLicenses) {
        Notification(
          'error',
          `${t('Allowed licenses exceeded for product')} ${get(
            c,
            'product.product_name',
          )}`,
          `${t('Remaining licenses')}: ${remainingLicenses}`,
        );
        throw new Error();
      }
      return true;
    });
  };

  const handleBundleOrderCreateClick = async () => {
    if (loading || generatingLicenses) {
      return false;
    }
    setGeneratingLicenses(true);

    try {
      const uuidID = uuid4().split('-').join('');

      const bundleCode = get(bundle, 'bundle.data.product.short_code');
      const isBundleKeyAuth =
        get(bundle, 'bundle.data.product.authorization_method') ===
        authMethods.key;

      if (!bundleCode) {
        Notification(
          'error',
          t('Please enter correct information and try again'),
        );
        return false;
      }

      let keys;
      if (isBundleKeyAuth) {
        keys = await generateLicenseKeys(bundleCode, 1);
      }

      const bundlePolicies = get(bundle, 'selected_bundle_policies', []);
      const bundleProducts = bundlePolicies
        .map((p) => {
          const policyProductID = get(p, 'product.id');
          const prodWrap = userProducts.find(
            (i) =>
              get(i, 'product.id') === policyProductID ||
              get(i, 'id') === policyProductID,
          );
          const productCode =
            get(p, 'product.short_code') ||
            get(prodWrap, 'product.short_code') ||
            get(prodWrap, 'short_code');

          if (!productCode || !get(p, 'code')) {
            return null;
          }

          return {
            license_template_code: get(p, 'code'),
            product_code: productCode,
          };
        })
        .filter(Boolean);

      if (!bundleProducts.length) {
        Notification(
          'error',
          t('Please enter correct information and try again'),
        );
        return false;
      }

      const bundleItem = {
        product_code: bundleCode,
        key: isBundleKeyAuth ? get(keys, '[0]') : undefined, // bundle license key
        users: [],
        max_license_users: isBundleKeyAuth
          ? undefined
          : Number(get(bundle, 'max_license_users') || 1),
        licenses: bundleProducts,
      };

      const addToAccount = get(formState, 'assignToAccount', false);
      const accountId = get(formState, 'customerAccountId', null);

      const data = {
        id: getResolvedOrderId(formState, uuidID),
        is_bundle: true,
        append: !!orderID,
        customer: customerData,
        items: [bundleItem],
        reference: get(formState, 'orderReference', ''),
        add_to_customer_account: addToAccount,
      };
      if (addToAccount && accountId) {
        data.customer_account_id = accountId;
      }
      handleOrderCreate(data);
      return true;
    } finally {
      setGeneratingLicenses(false);
    }
  };

  const handleOrderCreateClick = async () => {
    if (loading || generatingLicenses) {
      return false;
    }
    setGeneratingLicenses(true);

    const orderItems = await Promise.all(
      products.map(async (product) => {
        const licenseNum = Number(get(product, 'license_num', 1));
        const productCode = get(product, 'short_code');
        const isKeyAuth =
          get(product, 'authorization_method') === authMethods.key;

        let licenseKeys = null;
        if (isKeyAuth) {
          licenseKeys = await generateLicenseKeys(productCode, licenseNum);
        }
        let item;

        if (isKeyAuth && Array.isArray(licenseKeys) && licenseKeys.length) {
          // Prepare base license object
          const baseLicense = {
            license_template_code: get(product, 'selected_license_policy.code'),
          };

          // Add custom overrides if they exist
          if (
            get(product, 'custom_values_used') &&
            get(product, 'custom_overrides')
          ) {
            Object.assign(baseLicense, product.custom_overrides);
          }

          item = {
            product_id: get(product, 'id'),
            product_code: productCode,
            note: get(product, 'note'),
            licenses: licenseKeys.map((lk) => ({
              key: lk,
              ...baseLicense,
            })),
          };
        }

        if (!isKeyAuth) {
          // Prepare base license object
          const baseLicense = {
            license_template_code: get(product, 'selected_license_policy.code'),
            users: [],
          };

          // Add custom overrides if they exist
          if (
            get(product, 'custom_values_used') &&
            get(product, 'custom_overrides')
          ) {
            Object.assign(baseLicense, product.custom_overrides);
          }

          item = {
            product_id: get(product, 'id'),
            product_code: productCode,
            licenses: [baseLicense],
          };
        }

        return item;
      }),
    );

    try {
      validateLicenseNum(orderItems);
    } catch (error) {
      setGeneratingLicenses(false);
      return false;
    }

    const uuidID = uuid4().split('-').join('');

    const addToAccount = canManageCustomerAccounts
      ? get(formState, 'assignToAccount', false)
      : false;
    const accountId = canManageCustomerAccounts
      ? get(formState, 'customerAccountId', null)
      : null;

    const data = {
      id: getResolvedOrderId(formState, uuidID),
      append: !!orderID,
      customer: customerData,
      items: orderItems,
      reference: get(formState, 'orderReference', ''),
      add_to_customer_account: addToAccount,
    };
    if (addToAccount && accountId) {
      data.customer_account_id = accountId;
    }

    handleOrderCreate(data);
    setGeneratingLicenses(false);
    return true;
  };

  const orderReference = get(formState, 'orderReference');

  return (
    <div className="VerifyOrderStep">
      <section className="VerifyOrderStep-customer">
        {orderID || get(formState, 'id') ? (
          <div className="section-header order-id">
            {`${t('Order ID')}:`}
            <span>{get(formState, 'id')}</span>
          </div>
        ) : null}
        <div className="section-header reference">
          {orderReference && (
            <>
              {`${t('Order reference')}:`}
              <span>{orderReference}</span>
            </>
          )}
        </div>
        <div className="section-header">{t('Customer details')}</div>
        <DescriptionTable
          details={[
            { label: t('Email'), value: customerData.email },
            { label: t('First Name'), value: customerData.first_name },
            { label: t('Last Name'), value: customerData.last_name },
            { label: t('Company'), value: customerData.company_name },
            { label: t('Address'), value: customerData.address },
            { label: t('City'), value: customerData.city },
            { label: t('State / Province'), value: customerData.state },
            { label: t('Country'), value: customerData.country },
            { label: t('Zipcode / Postcode'), value: customerData.postcode },
            { label: t('Phone number'), value: customerData.phone },
            { label: t('Reference'), value: customerData.reference },
            {
              label: t('Is license manager'),
              value: customerData.is_manager ? t('Yes') : t('No'),
            },
          ]}
        />
      </section>
      <section className="VerifyOrderStep-products">
        <div className="section-header">{t('Order products')}</div>
        {isBundle ? (
          <SelectedBundlesTable
            companyID={companyID}
            selectedBundles={[bundle]}
            showActionBtns={false}
          />
        ) : (
          <SelectedProductsTable
            companyID={companyID}
            selectedProducts={products}
            showActionBtns={false}
          />
        )}
      </section>
      <div className="ProductsStep-actions">
        <Button
          disabled={loading || generatingLicenses}
          onClick={handleBackClick}
          size="lg"
          theme="default"
        >
          {t('Back')}
        </Button>
        <Button
          disabled={loading || generatingLicenses}
          onClick={() => {
            if (isBundle) {
              handleBundleOrderCreateClick();
              return true;
            }
            handleOrderCreateClick();
            return true;
          }}
          size="lg"
          theme="success"
        >
          {t('Create order')}
        </Button>
      </div>
    </div>
  );
};

VerifyOrderStep.propTypes = {
  companyID: PropTypes.number.isRequired,
  formState: PropTypes.object.isRequired,
  handleBack: PropTypes.func.isRequired,
  handleOrderCreate: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  orderID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

VerifyOrderStep.defaultProps = {
  loading: false,
  orderID: null,
};

export default VerifyOrderStep;
